import React, { useMemo, useRef, useState, useEffect } from "react";
import { observeCanvas } from "../../utils/canvas/dpr";
import { rollingAverage } from "../../utils/telemetry/smooth";
import { zoneColourFor, zoneFloors } from "../../utils/heartRateZones";
import { phaseKind, phaseSegments, segmentStats } from "../../utils/telemetry/phases";
import { cn } from "../../lib/utils";

// Colour tokens — chosen for dark-theme legibility and to match the in-
// workout dashboards (BikeDashboard cadence in cyan, RowerDashboard pace
// likewise; HR is the universal red of every fitness app since Garmin).
const COLORS = {
  hr: "#ef4444",
  power: "#facc15",
  secondary: "#22d3ee",
  axis: "rgba(255,255,255,0.18)",
  text: "rgba(255,255,255,0.55)",
  // Device-segment ribbon
  indoor_bike: "rgba(34,211,238,0.45)",
  rower: "rgba(251,146,60,0.45)",
  no_device: "rgba(255,255,255,0.08)",
};

const SMOOTH_OPTIONS = [
  { label: "Raw", value: 1 },
  { label: "3 s", value: 3 },
  { label: "10 s", value: 10 },
];

const seriesFor = (samples, type, smoothS) => {
  // For a HIIT session that switches devices, "cadence" and "spm" live in
  // different keys. The secondary series for the bike is cadence, for the
  // rower it's stroke rate; we draw whichever the row has.
  if (type === "secondary") {
    const merged = samples.map((row) => ({
      t: row.t,
      v: row.c != null ? row.c : row.spm != null ? row.spm : null,
    }));
    return rollingAverage(merged, "v", smoothS);
  }
  if (type === "power") return rollingAverage(samples, "w", smoothS);
  if (type === "hr") return rollingAverage(samples, "hr", smoothS);
  return [];
};

// Over the visible window only, so a zoomed-in stretch fills the height.
const minMax = (series, from = 0, to = series.length - 1) => {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = Math.max(0, Math.floor(from)); i <= Math.min(series.length - 1, Math.ceil(to)); i += 1) {
    const v = series[i];
    if (v == null) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return Number.isFinite(lo) ? { lo, hi } : null;
};

const LEFT_PAD = 8;
const RIGHT_PAD = 8;
const TOP_PAD = 8;
const BOTTOM_PAD = 16;
const MIN_SPAN_S = 20; // the most you can zoom in

const PHASE_BG = {
  work: "bg-work",
  rest: "bg-rest",
  warmup: "bg-warmup",
  cooldown: "bg-cooldown",
  transition: "bg-white/25",
  pause: "bg-white/10",
  other: "bg-white/40",
};

const clock = (seconds) => {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// `win` is the visible stretch in sample indexes ({ a, b }). Values off
// screen are still walked so the line runs to the edge; the canvas clips.
// `colorFor(value)`, when given, colours each segment by its value (heart
// rate by zone).
const drawSeries = (ctx, series, w, h, axis, color, win, colorFor = null) => {
  if (!axis) return;
  const span = axis.hi - axis.lo || 1;
  const plotW = w - LEFT_PAD - RIGHT_PAD;
  const plotH = h - TOP_PAD - BOTTOM_PAD;
  const xFor = (i) => LEFT_PAD + ((i - win.a) / (win.b - win.a || 1)) * plotW;
  const from = Math.max(0, Math.floor(win.a) - 1);
  const to = Math.min(series.length - 1, Math.ceil(win.b) + 1);

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  if (colorFor) {
    let prev = null;
    for (let i = from; i <= to; i += 1) {
      const v = series[i];
      if (v == null) {
        prev = null;
        continue;
      }
      const x = xFor(i);
      const y = TOP_PAD + plotH - ((v - axis.lo) / span) * plotH;
      if (prev) {
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(x, y);
        ctx.strokeStyle = colorFor(v);
        ctx.stroke();
      }
      prev = { x, y };
    }
    return;
  }

  let drawing = false;
  ctx.beginPath();
  for (let i = from; i <= to; i += 1) {
    const v = series[i];
    const x = xFor(i);
    if (v == null) {
      drawing = false;
      continue;
    }
    const y = TOP_PAD + plotH - ((v - axis.lo) / span) * plotH;
    if (!drawing) {
      ctx.moveTo(x, y);
      drawing = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
};

const drawDeviceRibbon = (ctx, samples, w, h, win) => {
  if (samples.length === 0) return;
  const plotW = w - LEFT_PAD - RIGHT_PAD;
  const xStep = plotW / (win.b - win.a || 1);
  const ribbonY = h - 14;
  const ribbonH = 6;
  const from = Math.max(0, Math.floor(win.a));
  const to = Math.min(samples.length - 1, Math.ceil(win.b));
  for (let i = from; i <= to; i += 1) {
    const dev = samples[i]?.dev || null;
    const fill =
      dev === "indoor_bike"
        ? COLORS.indoor_bike
        : dev === "rower"
        ? COLORS.rower
        : COLORS.no_device;
    ctx.fillStyle = fill;
    ctx.fillRect(LEFT_PAD + (i - win.a) * xStep, ribbonY, Math.max(1, xStep + 1), ribbonH);
  }
};

const SERIES = [
  { key: "hr", label: "HR", unit: "bpm", color: COLORS.hr },
  { key: "power", label: "Power", unit: "W", color: COLORS.power },
  { key: "secondary", color: COLORS.secondary },
];

// `zones`: the session's saved zone summary (max, resting, method), so the
// heart-rate line is coloured by zone as it was then (#194 follow-up).
// `phaseLog` and `plan` place the steps for sessions recorded before the
// recorder marked them (#200).
//
// Pinch or scroll to zoom, drag to pan; tap a step in the strip (or the
// chart) for its figures. Tap a key to hide or show its line.
const TelemetryChart = ({ samples, zones = null, phaseLog = null, plan = null, className }) => {
  const floors = useMemo(
    () => (zones?.max ? zoneFloors({ max: zones.max, resting: zones.resting, method: zones.method }) : null),
    [zones],
  );
  const canvasRef = useRef(null);
  const [smoothS, setSmoothS] = useState(3);
  const [hidden, setHidden] = useState(() => new Set());
  const [view, setView] = useState(null); // null = the whole session
  const [selected, setSelected] = useState(null);

  const n = samples?.length || 0;
  const full = useMemo(() => ({ a: 0, b: Math.max(1, n - 1) }), [n]);
  const win = view || full;
  const winRef = useRef(win);
  winRef.current = win;
  const t0 = samples?.[0]?.t || 0;

  useEffect(() => {
    setView(null);
    setSelected(null);
  }, [samples]);

  const series = useMemo(() => {
    if (!samples || samples.length === 0) return null;
    return {
      hr: seriesFor(samples, "hr", smoothS),
      power: seriesFor(samples, "power", smoothS),
      secondary: seriesFor(samples, "secondary", smoothS),
    };
  }, [samples, smoothS]);

  const axes = useMemo(() => {
    if (!series) return null;
    return {
      hr: minMax(series.hr, win.a, win.b),
      power: minMax(series.power, win.a, win.b),
      secondary: minMax(series.secondary, win.a, win.b),
    };
  }, [series, win.a, win.b]);

  const { segments, exact } = useMemo(
    () => phaseSegments(samples, { phaseLog, plan }),
    [samples, phaseLog, plan],
  );
  const chosen = selected != null ? segments[selected] : null;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const redraw = (ctx, w, h) => {
      ctx.clearRect(0, 0, w, h);
      if (!series || !axes) return;

      // Background grid lines (top, middle, bottom).
      ctx.strokeStyle = COLORS.axis;
      ctx.lineWidth = 1;
      const plotH = h - TOP_PAD - BOTTOM_PAD;
      for (const y of [TOP_PAD, TOP_PAD + plotH / 2, TOP_PAD + plotH]) {
        ctx.beginPath();
        ctx.moveTo(LEFT_PAD, y);
        ctx.lineTo(w - RIGHT_PAD, y);
        ctx.stroke();
      }

      ctx.save();
      ctx.beginPath();
      ctx.rect(LEFT_PAD, 0, w - LEFT_PAD - RIGHT_PAD, h);
      ctx.clip();

      // The chosen step, shaded behind the lines.
      if (chosen) {
        const plotW = w - LEFT_PAD - RIGHT_PAD;
        const span = win.b - win.a || 1;
        const x1 = LEFT_PAD + ((chosen.start - t0 - win.a) / span) * plotW;
        const x2 = LEFT_PAD + ((chosen.end - t0 - win.a) / span) * plotW;
        ctx.fillStyle = "rgba(255,255,255,0.08)";
        ctx.fillRect(x1, TOP_PAD, Math.max(2, x2 - x1), plotH);
      }

      // Device ribbon along the bottom.
      drawDeviceRibbon(ctx, samples, w, h, win);

      // Each series gets its own axis so the lines don't squash each other.
      if (!hidden.has("power")) drawSeries(ctx, series.power, w, h, axes.power, COLORS.power, win);
      if (!hidden.has("secondary")) {
        drawSeries(ctx, series.secondary, w, h, axes.secondary, COLORS.secondary, win);
      }
      if (!hidden.has("hr")) {
        drawSeries(ctx, series.hr, w, h, axes.hr, COLORS.hr, win, floors ? (v) => zoneColourFor(v, floors) : null);
      }
      ctx.restore();
    };
    return observeCanvas(canvas, redraw);
  }, [series, axes, samples, floors, hidden, win, chosen, t0]);

  // Pointer moves can outrun renders, so the ref is updated straight away.
  const applyView = (next) => {
    winRef.current = next || full;
    setView(next);
  };

  // Zoom keeping the point under the finger (fraction fx of the plot) still.
  const zoomAt = (fx, factor, shift = 0) => {
    const cur = winRef.current;
    const minSpan = Math.min(MIN_SPAN_S, full.b);
    const span = Math.min(full.b, Math.max(minSpan, (cur.b - cur.a) * factor));
    if (span >= full.b) {
      applyView(null);
      return;
    }
    const anchor = cur.a + fx * (cur.b - cur.a);
    const a = Math.max(0, Math.min(full.b - span, anchor - fx * span + shift));
    applyView({ a, b: a + span });
  };

  const plotFraction = (clientX) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const plotW = rect.width - LEFT_PAD - RIGHT_PAD;
    return Math.max(0, Math.min(1, (clientX - rect.left - LEFT_PAD) / plotW));
  };

  const pointers = useRef(new Map());
  const gesture = useRef(null);

  const onPointerDown = (e) => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Not a live pointer (a synthetic event): moves still reach the canvas.
    }
    pointers.current.set(e.pointerId, e.clientX);
    gesture.current = { x: e.clientX, moved: false, multi: pointers.current.size > 1 };
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const before = [...pointers.current.values()];
    pointers.current.set(e.pointerId, e.clientX);
    const after = [...pointers.current.values()];
    const g = gesture.current;
    if (g && Math.abs(e.clientX - g.x) > 6) g.moved = true;
    const rect = canvasRef.current.getBoundingClientRect();
    const plotW = rect.width - LEFT_PAD - RIGHT_PAD;
    const cur = winRef.current;
    if (after.length >= 2) {
      const d0 = Math.abs(before[0] - before[1]) || 1;
      const d1 = Math.abs(after[0] - after[1]) || 1;
      const mid0 = (before[0] + before[1]) / 2;
      const mid1 = (after[0] + after[1]) / 2;
      const shift = -((mid1 - mid0) / plotW) * (cur.b - cur.a);
      zoomAt(plotFraction(mid1), d0 / d1, shift);
    } else if (cur.b - cur.a < full.b) {
      const dx = after[0] - before[0];
      const span = cur.b - cur.a;
      const a = Math.max(0, Math.min(full.b - span, cur.a - (dx / plotW) * span));
      applyView({ a, b: a + span });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (pointers.current.size === 0) gesture.current = null;
    if (!g || g.moved || g.multi || !segments.length) return;
    // A tap picks the step under it.
    const cur = winRef.current;
    const t = t0 + cur.a + plotFraction(e.clientX) * (cur.b - cur.a);
    const i = segments.findIndex((s) => t >= s.start && t < s.end);
    setSelected(i >= 0 && i !== selected ? i : null);
  };

  // Scroll to zoom on a computer. Needs a non-passive listener to keep the
  // sheet from scrolling at the same time.
  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const onWheel = (e) => {
      if (!e.deltaY) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const fx = Math.max(0, Math.min(1, (e.clientX - rect.left - LEFT_PAD) / (rect.width - LEFT_PAD - RIGHT_PAD)));
      zoomAtRef.current(fx, Math.exp(e.deltaY * 0.002));
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [n]);

  if (!samples || samples.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-sm text-white/40 ${
          className || ""
        }`}
      >
        No telemetry recorded for this session.
      </div>
    );
  }

  const hasSecondaryRower = samples.some((r) => r.spm != null);
  const fullAxes = {
    hr: series && minMax(series.hr),
    power: series && minMax(series.power),
    secondary: series && minMax(series.secondary),
  };
  const toggle = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const span = win.b - win.a || 1;
  const pct = (t) => ((t - t0 - win.a) / span) * 100;
  const stats = chosen ? segmentStats(samples, chosen) : null;

  return (
    <div className={`flex flex-col gap-2 ${className || ""}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap items-center gap-1 text-xs">
          {SERIES.filter(({ key }) => fullAxes[key]).map(({ key, label, unit, color }) => (
            <Legend
              key={key}
              dot={color}
              label={key === "secondary" ? (hasSecondaryRower ? "Stroke rate" : "Cadence") : label}
              axis={axes?.[key]}
              unit={key === "secondary" ? (hasSecondaryRower ? "spm" : "rpm") : unit}
              off={hidden.has(key)}
              onClick={() => toggle(key)}
            />
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-full border border-white/15 p-0.5">
          {SMOOTH_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSmoothS(opt.value)}
              className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                smoothS === opt.value
                  ? "bg-white/15 text-white"
                  : "text-white/55 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative rounded-lg bg-white/[0.03] border border-white/10 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="block w-full h-56 select-none"
          style={{ touchAction: "pan-y" }}
          aria-label="Workout telemetry chart. Pinch or scroll to zoom, drag to move along."
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={(e) => {
            // The page took the touch to scroll: not a tap.
            pointers.current.delete(e.pointerId);
            gesture.current = null;
          }}
        />
        {view && (
          <button
            type="button"
            onClick={() => setView(null)}
            className="absolute right-2 top-2 rounded-full border border-white/15 bg-background/80 px-2.5 py-0.5 text-xs text-white/80 hover:text-white"
          >
            Reset zoom
          </button>
        )}
        {segments.length > 0 && (
          <div className="relative mx-2 mb-2 h-4 overflow-hidden rounded-sm bg-white/[0.04]" role="group" aria-label="Steps">
            {segments.map((seg, i) => {
              const left = pct(seg.start);
              const right = pct(seg.end);
              if (right <= 0 || left >= 100) return null;
              return (
                <button
                  key={`${seg.start}-${i}`}
                  type="button"
                  aria-label={`${seg.label || "Step"}, ${clock(seg.end - seg.start)}`}
                  aria-pressed={selected === i}
                  onClick={() => setSelected(selected === i ? null : i)}
                  className={cn(
                    "absolute inset-y-0 border-r border-background/70",
                    PHASE_BG[phaseKind(seg.type, seg.label)],
                    selected != null && selected !== i ? "opacity-40" : "opacity-90",
                    selected === i && "ring-2 ring-inset ring-white",
                  )}
                  style={{ left: `${left}%`, width: `${Math.max(0.4, right - left)}%` }}
                />
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-between text-[11px] tabular-nums text-white/40">
        <span>{clock(win.a)}</span>
        {segments.length > 0 && !exact && <span>Steps placed approximately</span>}
        {segments.length === 0 && !exact && <span>{"Step times weren't recorded"}</span>}
        <span>{clock(win.b)}</span>
      </div>

      {chosen && stats && (
        <div className="rounded-lg bg-white/5 px-3 py-2 text-xs" aria-live="polite">
          <div className="flex items-baseline justify-between gap-2">
            <span className="min-w-0 break-words text-sm font-semibold text-foreground">
              {chosen.label || "Step"}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {clock(chosen.start - t0)} · took {clock(stats.seconds)}
            </span>
          </div>
          {(stats.hr || stats.power) && (
            <div className="mt-1 flex flex-wrap gap-x-3 tabular-nums text-muted-foreground">
              {stats.hr && (
                <span>
                  HR avg <span className="text-foreground">{stats.hr.avg}</span>, peak{" "}
                  <span className="text-foreground">{stats.hr.max}</span> bpm
                </span>
              )}
              {stats.power && (
                <span>
                  Power avg <span className="text-foreground">{stats.power.avg}</span>, peak{" "}
                  <span className="text-foreground">{stats.power.max}</span> W
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <DeviceShadingKey
        bikeSeconds={samples.filter((r) => r.dev === "indoor_bike").length}
        rowerSeconds={samples.filter((r) => r.dev === "rower").length}
        noDeviceSeconds={samples.filter((r) => !r.dev).length}
      />
    </div>
  );
};

const Legend = ({ dot, label, axis, unit, off, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={!off}
    title={off ? `Show ${label}` : `Hide ${label}`}
    className={cn(
      "flex min-h-[28px] items-center gap-1.5 rounded-full px-2 transition-colors hover:bg-white/5",
      off ? "text-white/35" : "text-white/70",
    )}
  >
    <span
      className={cn("inline-block w-2 h-2 rounded-full", off && "opacity-30")}
      style={{ backgroundColor: dot }}
    />
    <span className={cn(off && "line-through")}>{label}</span>
    {axis && !off && (
      <span className="text-white/40 tabular-nums">
        {Math.round(axis.lo)}–{Math.round(axis.hi)} {unit}
      </span>
    )}
  </button>
);

const DeviceShadingKey = ({ bikeSeconds, rowerSeconds, noDeviceSeconds }) => {
  if (bikeSeconds + rowerSeconds + noDeviceSeconds === 0) return null;
  const items = [];
  if (bikeSeconds > 0)
    items.push({
      color: COLORS.indoor_bike,
      label: `Bike ${Math.round(bikeSeconds)} s`,
    });
  if (rowerSeconds > 0)
    items.push({
      color: COLORS.rower,
      label: `Rower ${Math.round(rowerSeconds)} s`,
    });
  if (noDeviceSeconds > 0)
    items.push({
      color: COLORS.no_device,
      label: `Floor / gap ${Math.round(noDeviceSeconds)} s`,
    });
  return (
    <div className="flex items-center gap-3 text-xs text-white/40">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-2 rounded-sm"
            style={{ backgroundColor: it.color }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
};

export default TelemetryChart;
